import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  IconButton,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Chip
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Link as LinkIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Audiotrack as AudioIcon,
  VideoLibrary as VideoIcon,
  Description as FileIcon
} from '@mui/icons-material';

interface MediaUploadProps {
  mediaType: 'image' | 'audio' | 'video' | 'file';
  value: string;
  onChange: (value: string) => void;
  onFileUpload?: (file: File) => Promise<string>;
  placeholder?: string;
  helperText?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

const MediaUpload: React.FC<MediaUploadProps> = ({
  mediaType,
  value,
  onChange,
  onFileUpload,
  placeholder,
  helperText
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const getMediaIcon = () => {
    switch (mediaType) {
      case 'image':
        return <ImageIcon />;
      case 'audio':
        return <AudioIcon />;
      case 'video':
        return <VideoIcon />;
      case 'file':
        return <FileIcon />;
      default:
        return <FileIcon />;
    }
  };

  const getMediaLabel = () => {
    switch (mediaType) {
      case 'image':
        return 'Imagem';
      case 'audio':
        return 'Áudio';
      case 'video':
        return 'Vídeo';
      case 'file':
        return 'Arquivo';
      default:
        return 'Arquivo';
    }
  };

  const getAcceptedTypes = () => {
    switch (mediaType) {
      case 'image':
        return 'image/*';
      case 'audio':
        return 'audio/*';
      case 'video':
        return 'video/*';
      case 'file':
        return '*/*';
      default:
        return '*/*';
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!onFileUpload) {
      setUploadError('Upload de arquivo não configurado');
      return;
    }

    setUploading(true);
    setUploadError('');

    try {
      const url = await onFileUpload(file);
      onChange(url);
    } catch (error: any) {
      setUploadError(error.message || 'Erro ao fazer upload do arquivo');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = async (event: React.DragEvent) => {
    event.preventDefault();
    
    const files = event.dataTransfer.files;
    if (files.length === 0) return;

    const file = files[0];
    if (!onFileUpload) {
      setUploadError('Upload de arquivo não configurado');
      return;
    }

    setUploading(true);
    setUploadError('');

    try {
      const url = await onFileUpload(file);
      onChange(url);
    } catch (error: any) {
      setUploadError(error.message || 'Erro ao fazer upload do arquivo');
    } finally {
      setUploading(false);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };

  const clearValue = () => {
    onChange('');
    setUploadError('');
  };

  return (
    <Box>
      <Tabs 
        value={activeTab} 
        onChange={(_, newValue) => setActiveTab(newValue)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        <Tab 
          label="URL" 
          icon={<LinkIcon />} 
          iconPosition="start"
        />
        <Tab 
          label="Upload" 
          icon={<UploadIcon />} 
          iconPosition="start"
          disabled={!onFileUpload}
        />
      </Tabs>

      <TabPanel value={activeTab} index={0}>
        <TextField
          label={`URL da ${getMediaLabel()}`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          fullWidth
          placeholder={placeholder || `https://exemplo.com/arquivo.${mediaType === 'image' ? 'jpg' : mediaType === 'audio' ? 'mp3' : mediaType === 'video' ? 'mp4' : 'pdf'}`}
          helperText={helperText || `Cole a URL da ${getMediaLabel().toLowerCase()} que será enviada`}
          InputProps={{
            startAdornment: getMediaIcon()
          }}
        />
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        {onFileUpload ? (
          <Paper
            sx={{
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              cursor: 'pointer',
              bgcolor: 'background.paper',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'action.hover'
              }
            }}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept={getAcceptedTypes()}
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              {uploading ? (
                <CircularProgress size={40} />
              ) : (
                <>
                  <UploadIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                  <Typography variant="h6" color="text.secondary">
                    Arraste e solte ou clique para selecionar
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {getMediaLabel()} será enviada para todos os leads elegíveis
                  </Typography>
                  <Chip 
                    label={`Tipo: ${getMediaLabel()}`} 
                    color="primary" 
                    variant="outlined"
                    icon={getMediaIcon()}
                    sx={{
                      borderColor: 'primary.main',
                      color: 'primary.main'
                    }}
                  />
                </>
              )}
            </Box>
          </Paper>
        ) : (
                  <Alert 
          severity="info"
          sx={{
            bgcolor: 'info.dark',
            color: 'info.contrastText',
            '& .MuiAlert-icon': {
              color: 'info.contrastText'
            }
          }}
        >
          Upload de arquivo não está configurado. Use a aba URL para inserir links externos.
        </Alert>
        )}
      </TabPanel>

      {uploadError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {uploadError}
        </Alert>
      )}

      {value && (
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip 
            label={`${getMediaLabel()} selecionada`} 
            color="success" 
            icon={getMediaIcon()}
          />
          <IconButton size="small" onClick={clearValue} color="error">
            <DeleteIcon />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

export default MediaUpload; 